import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { StatusChip, type StatusChipColor } from "components/StatusChip";
import { TruncateDataGridCell } from "components/TruncateDataGridCell";
import {
  CADENCE_LABELS,
  INTAKE_LABELS,
  STATUS_LABELS,
} from "encamp-shared/src/waste/vendorDataCompleteness";
import {
  WasteVendorDataCompletenessQuery,
  WasteVendorDataStatus,
  WasteVendorDeliveryCadence,
  WasteVendorIntake,
} from "generated-graphql/graphql";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { prettyPrintDateFull } from "util/dates";

export type VendorDataRow =
  WasteVendorDataCompletenessQuery["wasteVendorDataCompleteness"]["items"][number];

// Colour is the only thing this file owns — labels come from encamp-shared so
// the grid and the CSV export cannot drift on wording. NO_CADENCE is `info`
// because it prompts configuration rather than reporting a failure, and
// INACTIVE takes the neutral `default` role the way a CLOSED registration does
// (see waste/registration/statusBadgeConstants.ts).
export const STATUS_CHIP_COLORS: Record<string, StatusChipColor> = {
  OVERDUE: "error",
  NEVER_RECEIVED: "error",
  DUE: "warning",
  NO_CADENCE: "info",
  CURRENT: "success",
  INACTIVE: "default",
};

// Record<string, ...> views over the shared label maps: a cell value here is
// typed as the generated GraphQL enum, which is nominally narrower than the
// string-literal keys encamp-shared declares even though the runtime values
// are identical, so a generic-string lookup avoids an `as` cast.
const STATUS_LABEL_LOOKUP: Record<string, string> = STATUS_LABELS;
const CADENCE_LABEL_LOOKUP: Record<string, string> = CADENCE_LABELS;
const INTAKE_LABEL_LOOKUP: Record<string, string> = INTAKE_LABELS;

function renderDate(value: string | null | undefined): string {
  return value ? prettyPrintDateFull(value) : "—";
}

/**
 * `coveredThrough` is a pure calendar date built at UTC midnight (see
 * `vendorDataCompleteness.ts`), unlike `firstReceivedAt`/`lastReceivedAt`/
 * `lastPipelineRunAt`, which are genuine instants. Luxon's default local-zone
 * parse would render, e.g., `2026-06-30T00:00:00Z` as "June 29, 2026" for any
 * US viewer — a day earlier than the CSV export (which reads the UTC date
 * directly) and the date filter (which also compares on the UTC day). Pin
 * both the input and output zone to UTC so the grid, the drawer, the CSV, and
 * the filter all agree on the same calendar day.
 */
function renderCoveredThroughDate(value: string | null | undefined): string {
  return value ? prettyPrintDateFull(value, { inputZone: "utc", outputZone: "utc" }) : "—";
}

/** Falls back to the raw observed spellings when no canonical vendor matched. */
function vendorDisplayName(row: VendorDataRow): string {
  if (row.vendorName) return row.vendorName;
  if (row.observedNames.length > 0) return row.observedNames.join(", ");
  return "—";
}

// importCount, manifestCount, awaitingIngestCount, and firstReceivedAt DO
// have server-side omnisearch handlers in vendorDataCompleteness.ts —
// they're left out of the Filters dialog as a deliberate product choice, not
// because filtering on them would be a no-op. dueByDayOfMonth,
// lastPipelineRunAt, and notes are excluded because they genuinely have no
// handler there.
export const EXCLUDE_FILTER_COLUMNS = [
  "dueByDayOfMonth",
  "firstReceivedAt",
  "importCount",
  "manifestCount",
  "lastPipelineRunAt",
  "awaitingIngestCount",
  "notes",
];

// isActive and needsMapping are real, server-filterable fields with no
// dedicated grid column (their state is implied by status/chip colour), so
// they're offered as filters directly rather than via a hidden column.
export const ADDITIONAL_FILTER_KEYS: FilterKey[] = [
  { key: "isActive", header: "Active", filterKeyType: "boolean" },
  { key: "needsMapping", header: "Needs mapping", filterKeyType: "boolean" },
];

export interface VendorDataColumnHandlers {
  onEdit: (row: VendorDataRow) => void;
  onDelete: (row: VendorDataRow) => void;
}

// Columns capture callback props, so — unlike a module-level constant array —
// this is a factory the page memoizes with useMemo (see index.tsx).
export function vendorDataColumns({
  onEdit,
  onDelete,
}: VendorDataColumnHandlers): OmnisearchGridColDef<VendorDataRow>[] {
  return [
    {
      field: "tenantName",
      headerName: "Organization",
      flex: 0.7,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "vendorName",
      headerName: "Vendor",
      flex: 0.7,
      minWidth: 170,
      sortable: true,
      valueGetter: (_, row) => vendorDisplayName(row),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.45,
      minWidth: 150,
      sortable: true,
      filterKeyType: "enum",
      enumValues: Object.values(WasteVendorDataStatus),
      enumPresentationFunction: (value) => STATUS_LABEL_LOOKUP[value] ?? value,
      renderCell: ({ row }) => (
        <StatusChip
          label={STATUS_LABEL_LOOKUP[row.status] ?? row.status}
          color={STATUS_CHIP_COLORS[row.status] ?? "default"}
        />
      ),
    },
    {
      field: "periodsBehind",
      headerName: "Periods Behind",
      flex: 0.35,
      minWidth: 120,
      sortable: true,
      type: "number",
      filterKeyType: "number",
    },
    {
      field: "cadence",
      headerName: "Cadence",
      flex: 0.35,
      minWidth: 100,
      sortable: true,
      filterKeyType: "enum",
      enumValues: Object.values(WasteVendorDeliveryCadence),
      enumPresentationFunction: (value) => CADENCE_LABEL_LOOKUP[value] ?? value,
      valueGetter: (_, row) =>
        row.cadence ? (CADENCE_LABEL_LOOKUP[row.cadence] ?? row.cadence) : "—",
    },
    {
      field: "dueByDayOfMonth",
      headerName: "Due by",
      flex: 0.3,
      minWidth: 80,
      sortable: true,
      valueGetter: (_, row) => row.dueByDayOfMonth ?? "—",
    },
    {
      field: "intake",
      headerName: "Intake",
      flex: 0.45,
      minWidth: 170,
      sortable: true,
      filterKeyType: "enum",
      enumValues: Object.values(WasteVendorIntake),
      enumPresentationFunction: (value) => INTAKE_LABEL_LOOKUP[value] ?? value,
      valueGetter: (_, row) => (row.intake ? (INTAKE_LABEL_LOOKUP[row.intake] ?? row.intake) : "—"),
    },
    {
      field: "lastReceivedAt",
      headerName: "Last Received",
      flex: 0.4,
      minWidth: 140,
      sortable: true,
      filterKeyType: "date",
      renderCell: ({ value }) => renderDate(value),
    },
    {
      field: "coveredThrough",
      headerName: "Data through",
      flex: 0.4,
      minWidth: 140,
      sortable: true,
      filterKeyType: "date",
      renderCell: ({ value }) => renderCoveredThroughDate(value),
    },
    {
      field: "firstReceivedAt",
      headerName: "First seen",
      flex: 0.4,
      minWidth: 140,
      sortable: true,
      renderCell: ({ value }) => renderDate(value),
    },
    {
      field: "importCount",
      headerName: "Imports",
      flex: 0.3,
      minWidth: 90,
      sortable: true,
      type: "number",
    },
    {
      field: "manifestCount",
      headerName: "Manifests",
      flex: 0.3,
      minWidth: 100,
      sortable: true,
      type: "number",
    },
    {
      field: "lastPipelineRunAt",
      headerName: "Last Pipeline Run",
      flex: 0.4,
      minWidth: 150,
      sortable: true,
      renderCell: ({ value }) => renderDate(value),
    },
    {
      field: "awaitingIngestCount",
      headerName: "Awaiting Ingest",
      flex: 0.35,
      minWidth: 130,
      sortable: true,
      type: "number",
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 0.6,
      minWidth: 160,
      sortable: false,
      // Notes are free text and routinely longer than the column; ellipsize and
      // put the full value in a tooltip rather than letting it clip silently.
      renderCell: ({ value }) =>
        value ? (
          <TruncateDataGridCell variant="body2" showTooltip>
            {value}
          </TruncateDataGridCell>
        ) : (
          "—"
        ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "",
      sortable: false,
      getActions: ({ row }) => {
        // The whole row is clickable (it opens the editor), so each action must
        // stop propagation — otherwise Delete would open the confirm dialog AND
        // the editor behind it.
        const deleteAction = (
          <GridActionsCellItem
            key="delete"
            icon={<Delete />}
            label="Delete"
            onClick={(event) => {
              event.stopPropagation();
              onDelete(row);
            }}
            // A row with no expectation yet (an observed-only pair) has no
            // TenantWasteVendor id behind it — nothing for delete to remove.
            disabled={!row.isExpected}
          />
        );

        return [
          <GridActionsCellItem
            key="edit"
            icon={<Edit />}
            label="Edit"
            onClick={(event) => {
              event.stopPropagation();
              onEdit(row);
            }}
          />,
          row.isExpected ? (
            deleteAction
          ) : (
            <Tooltip key="delete" title="No expectation exists yet for this pair">
              <span>{deleteAction}</span>
            </Tooltip>
          ),
        ];
      },
    },
  ];
}
